import { plural } from '@/lib/format';
import type { ChannexProvisionResult, ChannexProvisionWarning } from '@/lib/api/types';

/**
 * Provisioning warnings, worded for the platform admin who pressed "publish".
 *
 * The backend sends codes, never sentences (the hotel cabinet translates the
 * same codes for hoteliers in `channex.warn.*`). These are what Booking.com and
 * friends will refuse to connect without, weeks later and looking like a mapping
 * fault — so they are said now, with what the hotel has to fill in.
 */
const WARNINGS: Record<string, (p: Record<string, string | number>) => string> = {
  'property.postalCode.empty': () => 'Не заполнен почтовый индекс отеля — Booking.com откажет в подключении. Пусть отель заполнит его в настройках, затем опубликуйте заново.',
  'property.state.empty': () => 'Не заполнена область или регион отеля — Booking.com откажет в подключении. После заполнения опубликуйте заново.',
  'property.coordinates.empty': () => 'Не заполнены координаты отеля. Нужны до первого подключения площадки.',
  'property.email.empty': () => 'Не заполнена почта отеля. Нужна до первого подключения площадки.',
  'property.phone.empty': () => 'Не заполнен телефон отеля. Нужен до первого подключения площадки.',
  'category.noRooms': (p) => `В категории «${p.category}» нет номеров — она опубликована с нулевой доступностью и не продаст ни одной ночи.`,
  'category.noPrice': (p) => `В категории «${p.category}» не задана цена — пока она не опубликована. Когда отель поставит цену, она уедет в Channex сама, в течение 15 минут.`,
  'ratePlan.noCategories': (p) => `Тариф «${p.ratePlan}» не включён ни в одну категорию — на площадки он не уедет.`,
  'ratePlan.disabledStillMapped': (p) => `Тариф «${p.ratePlan}» выключен, но площадка всё ещё продаёт его по старой связке. Ночи закрыты, но связку лучше снять в настройке канала.`,
  'ratePlan.currencyRecreated': (p) => `Тариф «${p.category} — ${p.plan}» пересоздан в Channex: валюта была ${p.from}, стала ${p.to}. Сопоставление канала по нему удалено — его нужно собрать заново в мастере подключения.`,
  'ratePlan.currencyLocked': (p) => `Тариф «${p.category} — ${p.plan}» нужно пересоздать в ${p.to} (сейчас ${p.from}), но Channex его не отдаёт: на него ссылается подключённый канал. Снимите тариф с сопоставления в канале и опубликуйте заново.`,
};

/** An unknown code comes back as the code itself — ugly on purpose: a missing text must be visible, not blank. */
export function channexWarningText(w: ChannexProvisionWarning): string {
  const text = WARNINGS[w.code];
  return text ? text(w.params ?? {}) : w.params ? `${w.code} ${JSON.stringify(w.params)}` : w.code;
}

/** What one run did, in a line for the toast: created vs. refreshed, counted. */
export function provisionSummary(r: ChannexProvisionResult): string {
  const created = (xs: Array<{ created: boolean }>) => xs.filter((x) => x.created).length;
  const part = (xs: Array<{ created: boolean }>, forms: [string, string, string]) => {
    const fresh = created(xs);
    return fresh ? `${plural(xs.length, forms)} (новых ${fresh})` : plural(xs.length, forms);
  };
  return [
    r.propertyCreated ? 'объект создан' : 'объект обновлён',
    part(r.roomTypes, ['тип номера', 'типа номера', 'типов номера']),
    part(r.ratePlans, ['тариф', 'тарифа', 'тарифов']),
  ].join(' · ');
}
